import store from 'store';

import { api } from '../api';

type UpdateBoardType = {
  id: number;
  name: string;
  viewOrder?: string;
  profileId: number;
};

type CreateBoardType = {
  name: string;
  viewOrder: string;
  profileId: number;
};

import type { BoardsByRivalIdReturnType } from 'api/endpoints/boards/boards.types';

export type GetBoardsByRivalIdQuery = {
  path: {
    rivalId: number;
  };
  query?: {
    visibilityGroupId: number;
  };
};

const injectedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBoardsByRivalId: build.query<
      BoardsByRivalIdReturnType,
      GetBoardsByRivalIdQuery
    >({
      query: ({ path: { rivalId }, query }) => ({
        url: `/rivals/${rivalId}/boards.json`,
        params: query,
      }),
      transformResponse: (returnValue: BoardsByRivalIdReturnType) => {
        return returnValue;
      },
    }),
    updateBoard: build.mutation<any, UpdateBoardType>({
      query: (board) => {
        const { id, profileId, ...body } = board;
        return {
          url: `/boards/${id}.json`,
          method: 'PUT',
          body,
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      onQueryStarted: async ({ id, profileId }, { queryFulfilled }) => {
        try {
          const { data: updatedBoard } = await queryFulfilled;
          store.dispatch.boards.populate({
            boards: {
              [id]: {
                ...updatedBoard,
                profile: profileId,
              },
            },
          });
        } catch {
          // Do nothing
        }
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
    createBoard: build.mutation<any, Partial<CreateBoardType>>({
      query: (body) => {
        return {
          url: `/boards.json`,
          method: 'POST',
          body,
          headers: {
            'Content-Type': 'application/json',
          },
        };
      },
      onQueryStarted: async ({ profileId }, { queryFulfilled }) => {
        try {
          const { data: updatedBoard } = await queryFulfilled;
          store.dispatch.boards.populate({
            boards: {
              [updatedBoard.id]: {
                ...updatedBoard,
                profile: profileId,
              },
            },
          });
        } catch {
          // Do nothing
        }
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
    deleteBoard: build.mutation<void, number>({
      query: (boardId) => ({
        url: `/boards/${boardId}.json`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      onQueryStarted: async (boardId, { queryFulfilled }) => {
        try {
          await queryFulfilled;
          store.dispatch.boards.delete({ boardIds: [boardId] });
        } catch {
          // Do nothing
        }
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
    //PUT /api/boards/:id/cards/rearrange.json
    rearrangeBoard: build.mutation<any, { boardId: number; cardIds: number[] }>(
      {
        query: ({ boardId, cardIds }) => ({
          url: `/boards/${boardId}/cards/rearrange.json`,
          method: 'PUT',
          body: { cardIds },
          headers: {
            'Content-Type': 'application/json',
          },
        }),
      },
    ),
  }),
});

export const enhancedApi = injectedApi.enhanceEndpoints({
  addTagTypes: ['Boards'],
});

export const {
  useGetBoardsByRivalIdQuery,
  useLazyGetBoardsByRivalIdQuery,
  useCreateBoardMutation,
  useDeleteBoardMutation,
  useUpdateBoardMutation,
  useRearrangeBoardMutation,
} = enhancedApi;
